import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSettingsStore } from '../../../store/settingsStore';
import { formatCurrency } from '../../../utils/format';
import { useLanguage } from '../../../features/language/useLanguage';

export interface FinancialGroupItem {
  key: string;
  label: string;
  value: number;
  icon: React.ReactNode;
  iconColor: string;
  iconBgColor: string;
  link: string;
  secondaryText?: string;
  subtext?: string;
  change?: number;
  onClick?: () => void;
}

interface FinancialGroupCardProps {
  title: string;
  items: FinancialGroupItem[];
  loading?: boolean;
  isFetching?: boolean;
  testId?: string;
}

export const FinancialGroupCard: React.FC<FinancialGroupCardProps> = ({
  title,
  items,
  loading,
  isFetching,
  testId,
}) => {
  const navigate = useNavigate();
  const { currencySymbol } = useSettingsStore();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="bg-white rounded-[24px] border border-gray-100 p-6 h-full">
        <div className="h-4 w-32 bg-gray-100 rounded animate-pulse mb-5" />
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-50 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div
      className={`bg-white rounded-[24px] border border-gray-100 p-6 h-full flex flex-col transition-opacity duration-200 ${
        isFetching ? 'opacity-60' : 'opacity-100'
      }`}
      data-test-id={testId}
    >
      <h3 className="text-md font-bold text-gray-700 uppercase tracking-wider mb-4 px-1">
        {title}
      </h3>
      <div className="flex flex-col gap-3 flex-1">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => (item.onClick ? item.onClick() : navigate(item.link))}
            className="group flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50/40 px-4 py-3.5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-transparent hover:bg-white hover:shadow-[0_12px_30px_rgba(0,0,0,0.05)] flex-1 min-h-[88px]"
            data-test-id={`dashboard-card-${item.key}`}
          >
            <div
              className={`p-2.5 rounded-xl ${item.iconBgColor} ${item.iconColor} shrink-0 transition-transform group-hover:scale-110`}
            >
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-gray-500 mb-1 truncate">{item.label}</p>
              <p className="text-lg font-bold text-gray-900 tracking-tight truncate">
                {formatCurrency(item.value, currencySymbol)}
              </p>
              {(item.change !== undefined || item.subtext || item.secondaryText) && (
                <div className="mt-1 flex items-center">
                  {item.change !== undefined ? (
                    <span
                      className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                        item.change >= 0
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'bg-rose-50 text-rose-600'
                      }`}
                    >
                      {item.change >= 0 ? '+' : ''}
                      {Math.round(item.change)}%
                    </span>
                  ) : item.subtext ? (
                    <span className="text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full truncate">
                      {item.subtext}
                    </span>
                  ) : item.secondaryText ? (
                    <span className="text-[11px] font-medium text-gray-500 truncate">
                      {t('dashboard.financialStats.totalPrefix')} {item.secondaryText}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
            <ArrowUpRight className="h-4 w-4 text-gray-300 group-hover:text-gray-900 transition-colors shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
};
